import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, FormControlLabel, Checkbox,
} from '@mui/material';
import { updateAccountIdentity } from '../../api/accounts';
import styles from './AccountIdentityCorrectionDialog.module.css';

// Super-admin only: correct the account identity fields the normal edit path
// cannot touch. Mirrors the backend contract in
// .claude/references/account-identity-correction.md (ADR-001). The backend is
// authoritative for RBAC, the (last4, sub) uniqueness guard, and the
// expense-factor flip cascade; this dialog surfaces the flip warning + confirm.

const ACCOUNT_TYPE_OPTIONS = [
  { value: 'CHECKING', label: 'Checking' },
  { value: 'SAVINGS', label: 'Savings' },
  { value: 'MONEY_MARKET', label: 'Money Market' },
  { value: 'CERTIFICATE_OF_DEPOSIT', label: 'Certificate of Deposit' },
  { value: 'LINE_OF_CREDIT', label: 'Line of Credit' },
  { value: 'LOAN', label: 'Loan' },
  { value: 'HELOC', label: 'HELOC' },
  { value: 'CREDITCARD', label: 'Credit Card' },
];

// Mirror of the backend LIABILITY_ACCOUNT_TYPES — used only to preview the
// asset/liability flip so the warning can appear before submit. The backend
// re-derives authoritatively.
export const LIABILITY_TYPES = new Set(['LINE_OF_CREDIT', 'LOAN', 'HELOC', 'CREDITCARD']);
export const isLiabilityType = (t) => LIABILITY_TYPES.has(t);

const norm = (v) => (v === '' || v === undefined ? null : v);

export default function AccountIdentityCorrectionDialog({ open, account, onClose, onSaved }) {
  const orig = useMemo(() => ({
    account_number: account?.account_number ?? '',
    account_number_last4: account?.account_number_last4 ?? '',
    sub_account_number: account?.sub_account_number ?? '',
    account_type: account?.account_type ?? '',
  }), [account]);

  const [accountNumber, setAccountNumber] = useState(orig.account_number);
  const [last4, setLast4] = useState(orig.account_number_last4);
  const [sub, setSub] = useState(orig.sub_account_number);
  const [accountType, setAccountType] = useState(orig.account_type);
  const [subcategory, setSubcategory] = useState('');
  const [reason, setReason] = useState('');
  const [confirmFlip, setConfirmFlip] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setAccountNumber(orig.account_number);
      setLast4(orig.account_number_last4);
      setSub(orig.sub_account_number);
      setAccountType(orig.account_type);
      setSubcategory('');
      setReason('');
      setConfirmFlip(false);
      setError(null);
    }
  }, [open, orig]);

  const typeChanged = accountType !== orig.account_type;
  // Current asset/liability side comes from the stored flag when present, else
  // from the current type.
  const currentIsLiability = account?._asset === false
    || (account?._asset === undefined && isLiabilityType(orig.account_type));
  const flip = typeChanged && (isLiabilityType(accountType) !== currentIsLiability);

  const changedFields = [];
  if (norm(accountNumber) !== norm(orig.account_number)) changedFields.push('account_number');
  if (norm(last4) !== norm(orig.account_number_last4)) changedFields.push('account_number_last4');
  if (norm(sub) !== norm(orig.sub_account_number)) changedFields.push('sub_account_number');
  if (typeChanged) changedFields.push('account_type');

  const canSubmit = changedFields.length > 0
    && reason.trim().length > 0
    && (!typeChanged || subcategory.trim().length > 0)
    && (!flip || confirmFlip)
    && !saving;

  const handleSubmit = async () => {
    const identityData = { reason: reason.trim() };
    if (norm(accountNumber) !== norm(orig.account_number)) identityData.account_number = norm(accountNumber);
    if (norm(last4) !== norm(orig.account_number_last4)) identityData.account_number_last4 = norm(last4);
    if (norm(sub) !== norm(orig.sub_account_number)) identityData.sub_account_number = norm(sub);
    if (typeChanged) {
      identityData.account_type = accountType;
      identityData.subcategory = subcategory.trim();
    }
    if (flip) identityData.confirm_expense_factor_flip = true;

    setSaving(true);
    setError(null);
    try {
      await updateAccountIdentity(account.id, identityData);
      if (onSaved) await onSaved();
      onClose();
    } catch (e) {
      setError(e?.message || 'The identity correction could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Correct account identity</DialogTitle>
      <DialogContent>
        <p className={styles.subtitle}>
          Super-admin correction of identity fields. Changing these affects account
          matching, de-duplication, and every report — the change is audited.
        </p>

        <div className={styles.field}>
          <TextField
            label="Account number" value={accountNumber} fullWidth size="small"
            onChange={(e) => setAccountNumber(e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <TextField
            label="Account number (last 4)" value={last4} fullWidth size="small"
            helperText="Leave unchanged to auto-derive from the account number."
            onChange={(e) => setLast4(e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <TextField
            label="Sub-account number" value={sub} fullWidth size="small"
            helperText="Full designator with prefix, e.g. “S 0001” or “L 0001”."
            onChange={(e) => setSub(e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <TextField
            select label="Account type" value={accountType} fullWidth size="small"
            onChange={(e) => setAccountType(e.target.value)}
          >
            {ACCOUNT_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
        </div>

        {typeChanged && (
          <div className={styles.field}>
            <TextField
              label="Subcategory" value={subcategory} fullWidth size="small" required
              helperText={`Must be an existing ${isLiabilityType(accountType) ? 'liability' : 'asset'} subcategory.`}
              onChange={(e) => setSubcategory(e.target.value)}
            />
          </div>
        )}

        {flip && (
          <div className={styles.flipWarning} role="alert">
            <div className={styles.flipWarningTitle}>
              This changes the account from {currentIsLiability ? 'liability' : 'asset'} to{' '}
              {isLiabilityType(accountType) ? 'liability' : 'asset'}.
            </div>
            <p className={styles.flipWarningBody}>
              It will flip the sign of this account’s balances and re-sign its
              transactions — its net-worth and cash-flow contribution reverses.
            </p>
            <FormControlLabel
              control={(
                <Checkbox
                  checked={confirmFlip}
                  onChange={(e) => setConfirmFlip(e.target.checked)}
                />
              )}
              label="I understand and want to flip the asset/liability sign."
            />
          </div>
        )}

        <div className={styles.field}>
          <TextField
            label="Reason" value={reason} fullWidth required multiline minRows={2} size="small"
            helperText="Recorded on the audit trail for this correction."
            onChange={(e) => setReason(e.target.value)}
          />
        </div>

        {error && <div className={styles.error} role="alert">{error}</div>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
          {saving ? 'Saving…' : 'Apply correction'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
